import type { Chart } from '../types'
import { selectionRows, type ExportSelection } from './exportSelection'
import { redactCommercial } from './redact'
import { incompleteText, isIncompletePoint } from '../charts/partial'

export function csvCell(v: string | number): string {
  // Labels may come from a catalog or a renamed card. Quotes alone do not stop
  // Excel from executing a formula; only text gets this guard, never numeric returns.
  const s = typeof v === 'number' ? String(v).replace('.', ',')
    : /^[\s]*[=+@-]/.test(v) ? `'${v}` : v
  return /[";\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

// Build the trend CSV as a pure string (testable; the download wrapper in exporter.ts
// just Blobs it). chart.data is run through redactCommercial first so no commercial
// FIELD can ever be serialized into an export — the §3 wall on the export surface Olof
// actually touches. The CSV also only emits the declared series columns, so this is
// belt-and-suspenders, but it makes the guarantee explicit and regression-proof.
// MP-OS-210: with a `selection`, a "Selection" block (formula-guarded like every cell) and one
// empty line come before the table, so the file says what it covers.
export function buildTrendCsv(chart: Chart, selection?: ExportSelection): string {
  const data = redactCommercial(chart.data)
  const series = chart.series ?? ['value']
  const valueAt = (d: Chart['data'][number], s: string): number | string => {
    const value = chart.series ? d[s] : d.value
    return typeof value === 'number' && Number.isFinite(value) ? value : ''
  }
  // A week can be clipped at a month boundary. Keep its actual dates beside
  // the bucket label so the exported data cannot be mistaken for a full week.
  const withDates = data.some((d) => d.period_from != null || d.period_to != null || d.period_kind != null)
  // MP-OS-207: a verified 0 is written as 0, a missing day as an empty cell; with day coverage
  // from the API, a Coverage column says which periods have missing days.
  const withCoverage = data.some((d) => d.coverage != null)
  const coverage = (d: Chart['data'][number]): string =>
    isIncompletePoint(d) ? incompleteText(d) : 'complete'
  const header = ['Period', ...series.map((s) => (chart.series ? s : `value (${chart.unit})`)),
    ...(withDates ? ['From', 'To', 'Period kind'] : []), ...(withCoverage ? ['Coverage'] : [])]
  const lines = [
    ...(selection ? [...selectionRows(selection).map((row) => row.map(csvCell).join(';')), ''] : []),
    header.map(csvCell).join(';'),
    ...data.map((d) => [String(d.name), ...series.map((s) => valueAt(d, s)),
      ...(withDates ? [String(d.period_from ?? ''), String(d.period_to ?? ''), String(d.period_kind ?? '')] : []),
      ...(withCoverage ? [coverage(d)] : []),
    ].map(csvCell).join(';')),
  ]
  return '﻿' + lines.join('\r\n')
}
